import { mdiLoading, mdiSchoolOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import iziToast from 'izitoast';
import { PaletteIcon, SettingsIcon } from 'lucide-react';
import React, { createContext, useContext, useReducer, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import { AppAgentContext } from 'src/context/ContextProvider';
import useIgnoreRenderEffect from 'src/hooks/useIgnoreRenderEffect';
import {
    deployAgentService,
    getAgentByIdService,
    learnAgentService,
    upsertAgentService,
} from 'src/services/AgentService';
import { appActions, DataStates, State } from 'src/store';
import { AiAgent } from 'src/types/AiAgent';
import { ModalType } from 'src/types/Modal';
import { compareArrayKnowledge, generateJsBotFile } from 'src/utils';
import { FileProps } from '../../shared/DragAndDrop';
import { LoadingComponent } from '../../shared/LoadingComponent';
import { TabsComponent } from '../../shared/TabsComponent';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '../../ui/breadcrumb';
import { AgentDesignComponent } from './AgentDesignComponent';
import { AgentKnowledgeComponent } from './AgentKnowledgeComponent';
import { AgentLayoutViewComponent } from './AgentLayoutViewComponent';
import { AgentSettingsComponent } from './AgentSettingsComponent';
import { Switch } from 'src/components/ui/switch';

export interface AiAgentContextType {
    state: {
        ai_agent: AiAgent;
        loading: {
            ai_agent: boolean;
        };
        initial_web: string[];
        initial_video: string[];
        initial_audio: string[];
        initial_files: FileProps[];
        final_files: FileProps[];
    };
    dispatch: React.Dispatch<any>;
}

const initialState: AiAgentContextType = {
    state: {
        ai_agent: new AiAgent({}),
        initial_audio: [],
        initial_video: [],
        initial_web: [],
        initial_files: [],
        final_files: [],
        loading: {
            ai_agent: false,
        },
    },
    dispatch: () => {},
};

// Create a slice for the context
export const { reducer, actions: AiAgentActions } = createSlice({
    name: 'AiAgentContextReducer',
    initialState,
    reducers: {
        setAiAgent: (state, action) => {
            state.state.ai_agent = action.payload;
        },
        loadingOn: (state, action: PayloadAction<keyof AiAgentContextType['state']['loading']>) => {
            state.state.loading[action.payload] = true;
        },
        loadingOff: (state, action: PayloadAction<keyof AiAgentContextType['state']['loading']>) => {
            state.state.loading[action.payload] = false;
        },
        setInitialFiles: (state, action: PayloadAction<FileProps[]>) => {
            state.state.initial_files = action.payload;
        },
        setFinalFiles: (state, action: PayloadAction<FileProps[]>) => {
            state.state.final_files = action.payload;
        },
        setInitialWeb: (state, action: PayloadAction<string[]>) => {
            state.state.initial_web = action.payload;
        },
        setInitialAudio: (state, action: PayloadAction<string[]>) => {
            state.state.initial_audio = action.payload;
        },
        setInitialVideo: (state, action: PayloadAction<string[]>) => {
            state.state.initial_video = action.payload;
        },
    },
});

// Create the context
export const AiAgentContext = createContext<AiAgentContextType>(initialState);

export const AgentDetailPageComponent = () => {
    const [{ state }, dispatch] = useReducer(reducer, initialState);
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const {
        state: { session },
        dispatch: dispatchApp,
    } = useContext(AppAgentContext);

    useIgnoreRenderEffect(async () => {
        if (id) {
            dispatch(AiAgentActions.loadingOn('ai_agent'));
            const response = await getAgentByIdService(id);
            if (response.success) {
                dispatch(AiAgentActions.setAiAgent(response.item));
                dispatch(AiAgentActions.setInitialWeb(response.item.getUrlWeb()));
                dispatch(AiAgentActions.setInitialAudio(response.item.getUrlAudio()));
                dispatch(AiAgentActions.setInitialVideo(response.item.getUrlVideo()));
                dispatch(AiAgentActions.loadingOff('ai_agent'));
            } else {
                // dispatch(appActions.onLoading({ type: DataStates.TEMPLATE, status: State.ERROR }));
                // navigate('/');
                dispatch(AiAgentActions.loadingOff('ai_agent'));
            }
        }
    }, [id]);

    if (state.loading.ai_agent) {
        return <LoadingComponent />;
    }

    async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        const agent = new AiAgent(state.ai_agent);
        setIsLoading(true);
        if (!id) {
            delete (agent as any).id;
        }

        const resp = await upsertAgentService(agent);
        if (resp.success) {
            setIsLoading(false);
            if (id) {
                iziToast.success({
                    message: 'Agente Actualizado',
                    position: 'topCenter',
                });
                const compare_files = compareArrayKnowledge(state.initial_files, state.final_files);
                const compare_web = compareArrayKnowledge(state.initial_web, state.ai_agent.getUrlWeb());
                const compare_audio = compareArrayKnowledge(state.initial_audio, state.ai_agent.getUrlAudio());
                const compare_video = compareArrayKnowledge(state.initial_video, state.ai_agent.getUrlVideo());

                if (compare_files || compare_web || compare_audio || compare_video) {
                    await learnAgentService(state.ai_agent.id);
                    dispatch(AiAgentActions.setInitialFiles(state.final_files));
                    dispatch(AiAgentActions.setInitialWeb(state.ai_agent.getUrlWeb()));
                    dispatch(AiAgentActions.setInitialAudio(state.ai_agent.getUrlAudio()));
                    dispatch(AiAgentActions.setInitialVideo(state.ai_agent.getUrlVideo()));
                }
            } else {
                iziToast.success({
                    message: 'Agente Creado',
                    position: 'topCenter',
                });
                navigate(`/agent_detail/${resp.item.id}`);
            }
        } else {
            setIsLoading(false);
            if (id) {
                iziToast.warning({
                    message: 'Hubo un problema al actualizar el agente',
                    position: 'topCenter',
                });
            } else {
                iziToast.warning({
                    message: 'Hubo un problema al crear el agente',
                    position: 'topCenter',
                });
            }
        }
    }

    const onDeploy = async () => {
        dispatchApp(
            appActions.openModal({
                type: ModalType.CONFIRMATION,
                data: {
                    title: 'Publicar',
                    description: '¿Estas seguro que quieres publicar el agente?',
                    state: DataStates.AGENT,
                    async onConfirm() {
                        dispatchApp(appActions.onLoading({ type: DataStates.AGENT, status: State.LOADING }));
                        const res = await deployAgentService(state.ai_agent.id, session.user_id);
                        if (res.success) {
                            iziToast.success({
                                message: 'Agente Publicado',
                                position: 'topCenter',
                            });
                            dispatchApp(appActions.closeModal(ModalType.CONFIRMATION));
                            dispatchApp(appActions.onLoading({ type: DataStates.AGENT, status: State.SUCCESS }));
                        } else {
                            iziToast.warning({
                                message: 'Hubo un problema al publicar al agente',
                                position: 'topCenter',
                            });
                            dispatchApp(appActions.closeModal(ModalType.CONFIRMATION));
                            dispatchApp(appActions.onLoading({ type: DataStates.AGENT, status: State.ERROR }));
                        }
                    },
                    onClose() {
                        dispatchApp(appActions.closeModal(ModalType.CONFIRMATION));
                    },
                },
            })
        );
    };

    const selectAssessor = () => {
        const assessors = session.ai_agent_config.assesors || [];
        dispatchApp(
            appActions.openModal({
                type: ModalType.DYNAMIC,
                data: {
                    title: 'Opciones',
                    className: 'bg-white max-w-[450px]',
                    onClose() {
                        dispatchApp(appActions.closeModal(ModalType.DYNAMIC));
                    },
                    state: { assessor: undefined, whatsApp: false, microphone: false, withAssessor: true },
                    children: (
                        state: {
                            assessor: { label: string; value: string };
                            whatsApp: boolean;
                            microphone: boolean;
                            withAssessor: boolean;
                        },
                        setState
                    ) => {
                        return (
                            <div>
                                <div className="relative mt-4">
                                    <Select
                                        value={state.assessor}
                                        options={assessors}
                                        maxMenuHeight={150}
                                        onChange={(option) => {
                                            setState({ ...state, assessor: option });
                                        }}
                                        styles={{
                                            menuPortal: (base) => ({
                                                ...base,
                                                left: 'auto',
                                                top: 'auto',
                                            }),
                                            menu: (provided) => ({
                                                ...provided,
                                                bottom: 'auto',
                                            }),
                                        }}
                                        menuPosition="fixed"
                                        menuPlacement="auto"
                                        placeholder={'Seleccionar Asesor'}
                                    />
                                </div>
                                <div className="flex justify-between">
                                    <div className="flex gap-1 items-center mt-4">
                                        <label htmlFor="">WhatsApp</label>
                                        <Switch
                                            className={`data-[state=checked]:bg-primary data-[state=unchecked]:bg-gray-200 scale-90`}
                                            checked={state.whatsApp}
                                            onClick={() => {
                                                setState({ ...state, whatsApp: !state.whatsApp });
                                            }}
                                        />
                                    </div>
                                    <div className="flex gap-1 items-center mt-4">
                                        <label htmlFor="">Microfono</label>
                                        <Switch
                                            className={`data-[state=checked]:bg-primary data-[state=unchecked]:bg-gray-200 scale-90`}
                                            checked={state.microphone}
                                            onClick={() => {
                                                setState({ ...state, microphone: !state.microphone });
                                            }}
                                        />
                                    </div>
                                    <div className="flex gap-1 items-center mt-4">
                                        <label htmlFor="">Asesor</label>
                                        <Switch
                                            className={`data-[state=checked]:bg-primary data-[state=unchecked]:bg-gray-200 scale-90`}
                                            checked={state.withAssessor}
                                            onClick={() => {
                                                setState({ ...state, withAssessor: !state.withAssessor });
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end mt-5">
                                    <button
                                        className="btn btn-sm"
                                        disabled={!state.assessor}
                                        type="button"
                                        onClick={() =>
                                            generateBot(
                                                state.assessor.value,
                                                state.whatsApp,
                                                state.microphone,
                                                state.withAssessor
                                            )
                                        }
                                    >
                                        Subir Script
                                    </button>
                                </div>
                            </div>
                        );
                    },
                },
            })
        );
    };

    const generateBot = (
        assesor_id: string | number,
        isWhatsApp: boolean,
        isMicrophone: boolean,
        withAssessor: boolean
    ) => {
        dispatchApp(appActions.closeModal(ModalType.DYNAMIC));
        if (state.ai_agent.id) {
            generateJsBotFile(
                state.ai_agent.id,
                state.ai_agent.name.split(' ').join('_'),
                session.ai_agent_config?.folders?.scripts ?? '',
                assesor_id,
                session.origin.concat('/www/', session.domainName, '/ai-agent/'),
                isWhatsApp,
                isMicrophone,
                withAssessor,
                {
                    pdf: false,
                    image: false,
                    excel: false,
                    text: false,
                    word: false
                },
                true,
                session.countries,
                '+57',
                (state) => {
                    switch (state) {
                        case State.LOADING:
                            iziToast.info({
                                message: 'Generando script...',
                                position: 'bottomLeft',
                            });
                            break;
                        case State.SUCCESS:
                            iziToast.success({
                                message: 'Script generado.',
                                position: 'bottomLeft',
                            });
                            break;
                        case State.ERROR:
                            iziToast.warning({
                                message: 'Hubo un problema al generar el script',
                                position: 'bottomLeft',
                            });
                            break;
                    }
                },
                'v1'
            );
        }
    };

    return (
        <AiAgentContext.Provider value={{ state, dispatch }}>
            <div className="mt-5 px-2">
                <div className="flex justify-between mb-3">
                    <div className="flex items-center">
                        <Breadcrumb>
                            <BreadcrumbList>
                                <BreadcrumbItem>
                                    <BreadcrumbLink href="/">Inicio</BreadcrumbLink>
                                </BreadcrumbItem>
                                <BreadcrumbSeparator />
                                <BreadcrumbItem>
                                    <BreadcrumbLink>
                                        <Link to="/agent">Agentes</Link>
                                    </BreadcrumbLink>
                                </BreadcrumbItem>
                                <BreadcrumbSeparator />
                                <BreadcrumbItem>
                                    <BreadcrumbPage>{id ? state.ai_agent.name : 'Crear'}</BreadcrumbPage>
                                </BreadcrumbItem>
                            </BreadcrumbList>
                        </Breadcrumb>
                    </div>
                    <div className="bg-white p-2 flex gap-2 z-[2]">
                        <button
                            form="agent_form"
                            type="submit"
                            className="btn btn-sm flex gap-2"
                            disabled={
                                state.ai_agent.name === '' ||
                                state.ai_agent.context === '' ||
                                state.ai_agent.getSelectModel().value === '' ||
                                state.ai_agent.getSelectVectorStore().value === '' ||
                                state.ai_agent.chat_history_context === '' ||
                                isLoading
                            }
                        >
                            {id ? 'Actualizar Agente' : 'Crear Agente'}
                            {isLoading && <Icon className="mr-1" size={1} path={mdiLoading} spin />}
                        </button>
                        {id && (
                            <>
                                <button className="btn btn-sm flex gap-2" onClick={selectAssessor}>
                                    Subir script
                                </button>

                                <button className="btn btn-sm-warning flex gap-2" onClick={onDeploy}>
                                    Publicar
                                </button>
                            </>
                        )}
                    </div>
                </div>
                <div className="flex">
                    <form className="w-[60%]" id="agent_form" onSubmit={onSubmit}>
                        <TabsComponent
                            tabsPosition="left"
                            tabs={[
                                {
                                    label: 'Opciones',
                                    component: <AgentSettingsComponent />,
                                    disabled: false,
                                    icon: <SettingsIcon />,
                                    visible: true,
                                },
                                {
                                    label: 'Conocimiento',
                                    component: <AgentKnowledgeComponent />,
                                    disabled: false,
                                    icon: <Icon path={mdiSchoolOutline} className="w-[24px]" />,
                                    visible: !location.pathname.includes('create'),
                                },
                                {
                                    label: 'Diseño',
                                    component: <AgentDesignComponent />,
                                    disabled: false,
                                    icon: <PaletteIcon />,
                                    visible: !location.pathname.includes('create'),
                                },
                            ]}
                            refreshContent={false}
                        />
                    </form>
                    <div className="w-[40%] flex justify-center items-center">
                        <AgentLayoutViewComponent />
                    </div>
                </div>
            </div>
        </AiAgentContext.Provider>
    );
};
